import { useEffect, useState } from 'react';

import { Check, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';

import { useUpdateProjectMutation } from '../api/master.api';
import type { ProjectDetail } from '../api/types';

interface Props {
  subProject: ProjectDetail;
}

const SubProjectDescriptionCard = ({ subProject }: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(subProject.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [updateProject, { isLoading }] = useUpdateProjectMutation();

  useEffect(() => {
    setDraft(subProject.description ?? '');
  }, [subProject.description]);

  const handleEdit = () => {
    setError(null);
    setDraft(subProject.description ?? '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      await updateProject({ id: subProject.id, patch: { description: draft } }).unwrap();
      setIsEditing(false);
    } catch {
      setError('No se pudo guardar la descripción. Intenta nuevamente.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{subProject.name}</CardTitle>
        <CardAction className="flex gap-1">
          {isEditing ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  void handleSave();
                }}
                disabled={isLoading}
              >
                <Check className="size-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={handleCancel} disabled={isLoading}>
                <X className="size-4" />
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="icon" onClick={handleEdit}>
              <Pencil className="size-4" />
            </Button>
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={5} disabled={isLoading} />
        ) : (
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">
            {subProject.description || <span className="italic">Sin descripción</span>}
          </p>
        )}
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
};

export default SubProjectDescriptionCard;
